/**
 * Google 日历配置（系统管理 → Google 日历配置）。
 *
 * 需求（2026-10-26）：HRMS 走查一-2 要求「面试排好后自动进各方 Google 日历」，
 * 这项能力依赖 Google Cloud 的 OAuth 客户端凭据。原来只能写进服务器 .env 再重建容器，
 * 改一个字母就要发一次版；线上到底配没配、回调地址填的是哪个，页面上完全看不出来。
 * 老板要求把它做成和「通信设置」「地图配置」一样的配置页面：在系统里填、保存即生效。
 *
 * 页面结构（沿用 GoogleMapsSettings 的骨架）：
 *   1. 状态卡：是否已配置、改了谁、什么时候改的、已有多少员工连了自己的日历
 *   2. 凭据卡：Client ID / Client Secret / 重定向 URI，加密落库、留空不修改
 *   3. 核对清单：Google Cloud 里需要配什么、回调地址要和哪一处逐字一致
 *
 * 刻意不做的事：
 *   · 不在本页发起 OAuth 授权跳转 —— 授权是「每个员工用自己的 Google 账号」做的，
 *     入口在招聘页 / 工程运营面板（POST /gcal/auth-url）。本页只管应用级凭据。
 *   · 不回显完整 Client Secret，只给脱敏值 + 「已配置 / 未配置」。
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ExternalLink,
  KeyRound,
  Link2,
  Loader2,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  Users,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400";

function FieldLabel({ children, hint }) {
  return (
    <div className="mb-1.5">
      <div className="text-sm font-semibold text-slate-700">{children}</div>
      {hint && <div className="mt-0.5 text-xs leading-5 text-slate-500">{hint}</div>}
    </div>
  );
}

/* 来源徽标：让管理员一眼看出「这个值是我在后台填的，还是服务器 .env 里带的」。
   排查「我明明改了怎么没生效」时，这一步最省时间。 */
function SourceBadge({ source, zh }) {
  const map = {
    db: [zh ? "后台填写" : "Saved here", "bg-brand-50 text-brand-700"],
    env: [zh ? "环境变量" : "From .env", "bg-slate-100 text-slate-500"],
    clear: [zh ? "已清除" : "Cleared", "bg-amber-50 text-amber-700"],
    missing: [zh ? "未配置" : "Not set", "bg-amber-50 text-amber-700"],
  };
  const [label, cls] = map[source] || map.missing;
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold ${cls}`} data-source={source}>
      {label}
    </span>
  );
}

export default function GoogleCalendarSettings() {
  const { api, lang } = useApp();
  const zh = lang === "zh";

  const [current, setCurrent] = useState(null);
  const [conn, setConn] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState("");
  const [err, setErr] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/google-calendar/config");
      setCurrent(data);
      setRedirectUri(data.redirectUri?.value || data.redirectUri?.effective || "");
      // Client ID 不是机密，后端会回传当前值，页面预填方便核对与轮换。
      setClientId(data.clientId?.value || "");
      setClientSecret(""); // Secret 永不回显，留空 = 不修改
      setFieldError("");
    } catch (e) {
      setCurrent(null);
      setErr(e?.response?.data?.detail || e?.message || "Failed");
    }
  }, [api]);

  const loadConnections = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/google-calendar/connections");
      setConn(data);
    } catch {
      setConn(null); // 连接列表读不到不影响凭据配置
    }
  }, [api]);

  useEffect(() => { load(); loadConnections(); }, [load, loadConnections]);

  const dirty =
    !!current &&
    (clientId.trim() !== String(current.clientId?.value || "").trim()
      || !!clientSecret.trim()
      || redirectUri.trim() !== String(current.redirectUri?.value || "").trim());

  const save = async () => {
    if (!current || saving || !dirty) return;

    const body = {};
    const nextId = clientId.trim();
    const nextSecret = clientSecret.trim();
    const nextRedirect = redirectUri.trim();

    if (nextId && nextId !== String(current.clientId?.value || "").trim()) body.clientId = nextId;
    if (nextSecret) body.clientSecret = nextSecret;
    if (nextRedirect !== String(current.redirectUri?.value || "").trim()) body.redirectUri = nextRedirect;

    if (!nextRedirect) {
      setFieldError(zh ? "重定向 URI 必须填写，否则授权回来会报 redirect_uri_mismatch。" : "Redirect URI is required.");
      return;
    }
    if (nextId && !nextId.endsWith(".apps.googleusercontent.com")) {
      setFieldError(zh ? "Client ID 应以 .apps.googleusercontent.com 结尾。" : "Client ID must end with .apps.googleusercontent.com");
      return;
    }

    setFieldError("");
    setSaving(true);
    try {
      const { data } = await api.put("/admin/google-calendar/config", body);
      setCurrent(data);
      setClientSecret("");
      setRedirectUri(data.redirectUri?.value || data.redirectUri?.effective || "");
      toast.success(zh ? "Google 日历凭据已保存，即时生效（无需重启）" : "Saved. Effective immediately, no restart needed.");
      loadConnections();
    } catch (e) {
      setFieldError(e?.response?.data?.detail || (zh ? "保存失败，请重试。" : "Could not save."));
    }
    setSaving(false);
  };

  const clearField = async (field) => {
    if (clearing) return;
    setClearing(true);
    try {
      const { data } = await api.put("/admin/google-calendar/config", { clear: [field] });
      setCurrent(data);
      if (field === "clientId") setClientId(data.clientId?.value || "");
      if (field === "clientSecret") setClientSecret("");
      if (field === "redirectUri") setRedirectUri("");
      toast.success(zh ? "已清除，恢复为环境变量值" : "Cleared, reverted to env value");
      loadConnections();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "清除失败" : "Could not clear"));
    }
    setClearing(false);
  };

  const configured = !!current?.configured;
  const encReady = current ? current.encryptionReady !== false : true;

  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="google-calendar-settings-page">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-black text-slate-900 md:text-2xl">
          <CalendarClock className="h-6 w-6 text-brand-600" />
          {zh ? "Google 日历配置" : "Google Calendar"}
        </h1>
        <p className="mt-1 text-xs leading-5 text-slate-500 md:text-sm">
          {zh
            ? "维护面试排期自动写入 Google 日历所用的 OAuth 客户端凭据。保存后立即生效，无需重启服务。"
            : "Manage the OAuth client credentials used to write interviews into Google Calendar. Changes take effect immediately."}
        </p>
      </div>

      {!current && !err && (
        <div className="flex min-h-48 items-center justify-center text-slate-400" data-testid="google-calendar-settings-loading">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      {err && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="google-calendar-settings-error">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700" />
          <p className="min-w-0 flex-1 text-sm text-amber-900">
            {zh ? `配置读取失败：${err}` : `Could not load settings: ${err}`}
          </p>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4" />{zh ? "重试" : "Retry"}
          </Button>
        </div>
      )}

      {current && (
        <>
          {/* 状态卡：整页的第一视觉落点 —— 现在到底能不能用 */}
          <section className="rounded-xl border border-slate-200 bg-white shadow-soft" data-testid="google-calendar-status" data-configured={configured ? "1" : "0"}>
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3">
              <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${configured ? "bg-brand-50 text-brand-700" : "bg-amber-50 text-amber-700"}`}>
                {configured ? <CheckCircle2 className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800">
                  {configured
                    ? (zh ? "凭据已配置，面试可自动写入日历" : "Credentials set. Interviews can sync to calendars.")
                    : (zh ? "凭据尚未配置，面试不会自动进日历" : "Not configured. Interviews will not sync to calendars.")}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {configured
                    ? (zh ? "每个员工还需各自点一次「连接 Google 日历」，之后由他排的面试才会自动进日历并发邀请给面试官与候选人。"
                      : "Each employee must connect their own Google Calendar once. Interviews they schedule then sync and email invites.")
                    : (zh ? "填好下方三项后保存即可；未配置时安排面试仍然正常，只是不会推送到日历。"
                      : "Fill in the three fields below. Scheduling still works without it — it just won't push to calendars.")}
                </p>
              </div>
              <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold ${configured ? "bg-brand-50 text-brand-700" : "bg-amber-50 text-amber-700"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${configured ? "bg-brand-500" : "bg-amber-500"}`} />
                {configured ? (zh ? "已配置" : "Set") : (zh ? "待配置" : "Required")}
              </span>
            </div>
            <div className="grid gap-3 px-4 py-3 text-xs text-slate-500 sm:grid-cols-3">
              <div>
                <span className="text-slate-400">{zh ? "最后更新：" : "Last updated: "}</span>
                {current.updatedAt ? new Date(current.updatedAt).toLocaleString() : (zh ? "尚未保存" : "Never")}
              </div>
              <div>
                <span className="text-slate-400">{zh ? "更新人：" : "Updated by: "}</span>
                {current.updatedBy || "—"}
              </div>
              <div data-testid="google-calendar-connections-count">
                <span className="text-slate-400">{zh ? "已连接日历的员工：" : "Connected employees: "}</span>
                {conn ? `${conn.total} ${zh ? "人" : ""}`.trim() : "—"}
              </div>
            </div>
          </section>

          {!encReady && (
            <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="google-calendar-enc-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {zh
                  ? "服务器缺少加密密钥（COMMS_ENC_KEY / ONBOARD_ENC_KEY / JWT_SECRET），凭据无法加密入库。请先补齐后再保存。"
                  : "The server has no encryption key configured, so credentials cannot be stored securely."}
              </span>
            </div>
          )}

          <section className="rounded-xl border border-slate-200 bg-white shadow-soft">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
              <KeyRound className="h-4 w-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-slate-800">{zh ? "OAuth 2.0 客户端" : "OAuth 2.0 client"}</h2>
            </div>
            <div className="space-y-4 p-4">
              <div>
                <FieldLabel
                  hint={current.clientId?.set
                    ? (zh ? `当前：${current.clientId.masked || "已配置"}。直接替换即可轮换。` : `Current: ${current.clientId.masked || "set"}`)
                    : (zh ? "在 Google Cloud → Google Auth Platform → 客户端 里创建「Web 应用」后复制。" : "Create a Web application client in Google Cloud and paste its ID.")}
                >
                  <label htmlFor="gcal-client-id" className="flex items-center gap-2">
                    {zh ? "Client ID" : "Client ID"}
                    <SourceBadge source={current.clientId?.source} zh={zh} />
                  </label>
                </FieldLabel>
                <input
                  id="gcal-client-id"
                  value={clientId}
                  onChange={(e) => { setClientId(e.target.value); setFieldError(""); }}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="xxxxx.apps.googleusercontent.com"
                  data-testid="google-calendar-client-id-input"
                  className={`h-10 w-full rounded-lg border border-slate-200 bg-white px-3 font-mono text-sm text-slate-800 placeholder:font-sans placeholder:text-slate-400 focus-visible:border-brand-400 ${FOCUS}`}
                />
              </div>

              <div>
                <FieldLabel
                  hint={current.clientSecret?.set
                    ? (zh ? `已配置（${current.clientSecret.masked}）。留空表示不修改，只有轮换时才需要重填。` : `Set (${current.clientSecret.masked}). Leave blank to keep it.`)
                    : (zh ? "与 Client ID 同时生成，创建时只显示一次；丢了可以在控制台重置。" : "Generated with the client ID; reset it in the console if lost.")}
                >
                  <label htmlFor="gcal-client-secret" className="flex items-center gap-2">
                    {zh ? "Client Secret" : "Client Secret"}
                    <SourceBadge source={current.clientSecret?.source} zh={zh} />
                  </label>
                </FieldLabel>
                <input
                  id="gcal-client-secret"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => { setClientSecret(e.target.value); setFieldError(""); }}
                  autoComplete="new-password"
                  spellCheck={false}
                  placeholder={current.clientSecret?.set ? (zh ? "留空 = 不修改" : "Leave blank to keep") : "GOCSPX-…"}
                  data-testid="google-calendar-client-secret-input"
                  className={`h-10 w-full rounded-lg border border-slate-200 bg-white px-3 font-mono text-sm text-slate-800 placeholder:font-sans placeholder:text-slate-400 focus-visible:border-brand-400 ${FOCUS}`}
                />
              </div>

              <div>
                <FieldLabel
                  hint={zh
                    ? `必须与 Google 控制台里登记的「已获授权的重定向 URI」逐字一致，路径固定为 ${current.redirectUri?.callbackPath}。`
                    : `Must match the authorized redirect URI in Google Cloud exactly. Path is fixed to ${current.redirectUri?.callbackPath}.`}
                >
                  <label htmlFor="gcal-redirect-uri" className="flex items-center gap-2">
                    {zh ? "重定向 URI" : "Redirect URI"}
                    <SourceBadge source={current.redirectUri?.source} zh={zh} />
                  </label>
                </FieldLabel>
                <input
                  id="gcal-redirect-uri"
                  value={redirectUri}
                  onChange={(e) => { setRedirectUri(e.target.value); setFieldError(""); }}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={current.redirectUri?.suggested || "https://api.example.com/api/public/gcal/callback"}
                  data-testid="google-calendar-redirect-input"
                  className={`h-10 w-full rounded-lg border border-slate-200 bg-white px-3 font-mono text-sm text-slate-800 placeholder:font-sans placeholder:text-slate-400 focus-visible:border-brand-400 ${FOCUS}`}
                />
                {current.redirectUri?.suggested && redirectUri.trim() !== current.redirectUri.suggested && (
                  <button
                    type="button"
                    onClick={() => { setRedirectUri(current.redirectUri.suggested); setFieldError(""); }}
                    data-testid="google-calendar-use-suggested"
                    className={`mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline ${FOCUS}`}
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    {zh ? `按当前环境填入：${current.redirectUri.suggested}` : `Use suggested: ${current.redirectUri.suggested}`}
                  </button>
                )}
              </div>

              {fieldError && (
                <p id="gcal-field-error" role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="google-calendar-field-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{fieldError}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                <Button
                  type="button"
                  onClick={save}
                  disabled={!dirty || saving || !encReady}
                  data-testid="google-calendar-save"
                  className="min-w-28"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {zh ? "保存配置" : "Save"}
                </Button>
                {current.clientSecret?.source === "db" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="outline" className="text-destructive hover:text-destructive" disabled={clearing} data-testid="google-calendar-clear-secret">
                        {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        {zh ? "清除 Secret" : "Clear secret"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{zh ? "清除已保存的 Client Secret？" : "Clear the saved client secret?"}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {zh
                            ? "清除后回退到服务器环境变量（若也没配，则面试不再自动进日历）。已连接的员工不受影响，但下次推送会失败。"
                            : "Reverts to the server env value. Interviews stop syncing if that is empty too."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{zh ? "取消" : "Cancel"}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => clearField("clientSecret")} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          {zh ? "确认清除" : "Clear"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          </section>

          {/* 已连接人员：谁连了自己的日历，才有资格说「排面试会自动进他的日历」 */}
          <section className="rounded-xl border border-slate-200 bg-white shadow-soft" data-testid="google-calendar-connections">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
              <Users className="h-4 w-4 text-slate-500" />
              <h2 className="text-sm font-semibold text-slate-800">{zh ? "已连接日历的员工" : "Connected employees"}</h2>
            </div>
            <div className="p-4">
              {!conn && <p className="text-xs text-slate-400">{zh ? "读取中…" : "Loading…"}</p>}
              {conn && conn.rows.length === 0 && (
                <p className="text-xs leading-5 text-slate-500" data-testid="google-calendar-connections-empty">
                  {zh
                    ? "还没有人连接。员工在「招聘 → 招聘看板」顶部点「连接 Google 日历」授权一次即可；工程同事也可在项目运营面板里连接。"
                    : "Nobody has connected yet. Employees can authorize from the Recruiting board header."}
                </p>
              )}
              {conn && conn.rows.length > 0 && (
                <ul className="divide-y divide-slate-100" data-testid="google-calendar-connections-list">
                  {conn.rows.map((r) => (
                    <li key={r.username} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-xs">
                      <span className="font-semibold text-slate-700">{r.name}</span>
                      <span className="text-slate-400">{r.username}</span>
                      <span className="break-all font-mono text-slate-500">{r.email || "—"}</span>
                      <span className="ml-auto text-slate-400">
                        {r.connectedAt ? new Date(r.connectedAt).toLocaleString() : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-slate-800">{zh ? "Google Cloud 设置核对" : "Google Cloud checklist"}</h2>
                <ul className="mt-2 space-y-1.5 text-xs leading-5 text-slate-500">
                  <li>{zh ? "确认 Google Calendar API 已启用（API 和服务 → 库 → Google Calendar API）。" : "Enable the Google Calendar API for the project."}</li>
                  <li>{zh ? "OAuth 同意屏幕的用户类型建议选「内部」：仅限公司 Workspace 账号，无需 Google 审核，授权不会 7 天过期。" : "Use an Internal user type: no Google review and no 7-day token expiry."}</li>
                  <li>{zh ? "把上面填的重定向 URI 原样加到「已获授权的重定向 URI」；末尾多一个斜杠也会 mismatch。" : "Add the redirect URI above verbatim; a trailing slash causes a mismatch."}</li>
                  <li>{zh ? "生产与测试如果共用同一个客户端，记得把两个回调地址都登记进去。" : "If production and test share the client, register both redirect URIs."}</li>
                </ul>
                <a
                  href="https://console.cloud.google.com/auth/clients"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
                >
                  {zh ? "打开 Google Cloud 客户端页面" : "Open Google Cloud clients"}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
